import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { Reveal } from "@/components/Reveal";
import ImageSlider from "@/components/ImageSlider";
import Seo from "@/components/Seo";
import ShareButtons from "@/components/ShareButtons";
import ContactButton from "@/components/ContactButton";
import { eur, AVAILABILITY, artText } from "@/lib/api";
import { useLang } from "@/context/LangContext";

export default function ProductDetail() {
  const { id } = useParams();
  const { artworks, loading, addToCart, settings } = useStore();
  const { t, lang } = useLang();
  const art = artworks.find((a) => a.artwork_id === id);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="font-mono text-[11px] tracking-[0.3em] text-bone-muted">{t("product.loading")}</p>
      </div>
    );
  }

  if (!art) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6">
        <p className="font-serif text-3xl italic text-bone-dim">{t("product.notFound")}</p>
        <Link data-testid="product-back-link" to="/shop" className="font-mono text-[11px] tracking-[0.3em] text-brass">
          {t("product.back")}
        </Link>
      </div>
    );
  }

  const availCls = AVAILABILITY[art.availability] || AVAILABILITY["In Atelier"];
  const priced = art.price > 0;
  const available = art.availability === "In Atelier" && priced;

  const specs = [
    [t("product.dimensions"), art.dimensions],
    [t("product.format"), t("product.formatValue")],
    ["MEDIUM", artText(art, "medium", lang)],
    [t("product.year"), art.year],
    ...(art.dwg_no ? [[t("product.drawingNo"), art.dwg_no]] : []),
    [t("product.framing"), t("product.framingValue")],
    [t("product.edition"), t("product.editionValue")],
  ];

  const title = artText(art, "title", lang);
  const description = artText(art, "description", lang) || t("seo.home");
  const origin = settings.site_url || window.location.origin;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: title,
    description,
    sku: art.dwg_no || art.artwork_id,
    brand: { "@type": "Brand", name: "DEMOIVRE" },
    url: `${origin}/shop/${art.artwork_id}`,
    ...(art.image_url ? { image: art.image_url.startsWith("http") ? art.image_url : `${origin}${art.image_url}` } : {}),
    ...(priced
      ? {
          offers: {
            "@type": "Offer",
            price: art.price,
            priceCurrency: "EUR",
            url: `${origin}/shop/${art.artwork_id}`,
            availability: art.availability === "In Atelier" ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
            itemCondition: "https://schema.org/NewCondition",
          },
        }
      : {}),
  };

  return (
    <div data-testid="product-page" className="mx-auto max-w-7xl px-6 pb-28 pt-32 sm:px-10 lg:px-16">
      <Seo title={title} description={description} image={art.image_url} type="product" jsonLd={jsonLd} />
      <Link
        data-testid="product-back-link"
        to="/shop"
        className="mb-10 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-bone-muted transition-colors hover:text-brass"
      >
        <ArrowLeft size={12} /> {t("product.collectionLabel")}
      </Link>

      <div className="grid gap-14 lg:grid-cols-2">
        <Reveal className="relative">
          <ImageSlider slides={art.slides?.length ? art.slides : (art.image_url ? [art.image_url] : [])} alt={title} />
          <div className="mt-4 flex items-center justify-between font-mono text-[9px] tracking-[0.25em] text-bone-muted">
            <span>{art.dwg_no}</span>
            <span>{art.dimensions}</span>
            <span className="text-brass">{t("product.originalCanvas")}</span>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <span className={`inline-block border px-3 py-1 font-mono text-[9px] tracking-[0.3em] ${availCls}`}>
            {t(`avail.${art.availability}`).toUpperCase()}
          </span>
          <h1 className="mt-6 font-serif text-4xl font-light leading-tight text-bone sm:text-5xl">
            {artText(art, "title", lang)}
          </h1>
          <p className="mt-4 font-mono text-2xl text-brass" data-testid="product-price">
            {priced ? eur(art.price) : t("product.priceOnRequest")}
          </p>
          <p className="mt-6 text-sm leading-relaxed text-bone-dim/85 sm:text-base">{artText(art, "description", lang)}</p>

          <div className="mt-8 border-l-2 border-brass/50 pl-6">
            <p className="font-mono text-[9px] tracking-[0.3em] text-brass">{t("product.idea")}</p>
            <p className="mt-3 font-serif text-lg italic leading-relaxed text-bone-dim">{artText(art, "story", lang)}</p>
          </div>

          <dl className="mt-10 divide-y divide-bone/10 border-y border-bone/10">
            {specs.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-3">
                <dt className="font-mono text-[9px] tracking-[0.3em] text-bone-muted">{k}</dt>
                <dd className="text-right font-mono text-[11px] tracking-[0.1em] text-bone">{v}</dd>
              </div>
            ))}
          </dl>

          <button
            data-testid="add-to-cart-button"
            disabled={!available}
            onClick={() => addToCart(art)}
            className={`mt-8 w-full border py-5 font-mono text-[11px] tracking-[0.35em] transition-all duration-300 ${
              available
                ? "border-brass/60 bg-brass/10 text-brass hover:bg-brass hover:text-ink"
                : "cursor-not-allowed border-bone/10 text-bone-muted"
            }`}
          >
            {available
              ? `${t("product.addTo")} — ${eur(art.price)}`
              : !priced
                ? t("product.priceOnRequest")
                : art.availability === "Reserved"
                  ? t("product.reserved")
                  : art.availability === "Unavailable"
                    ? t("product.unavailable")
                    : t("product.acquired")}
          </button>
          <p className="mt-4 text-center font-mono text-[9px] tracking-[0.25em] text-bone-muted">
            {t("product.certNote")}
          </p>
          <div className="mt-5 flex justify-center">
            <ContactButton testId="product-contact-button" subject={`DeMoivre — ${title}${art.dwg_no ? ` (${art.dwg_no})` : ""}`} />
          </div>
          <p className="mt-4 text-center">
            <Link
              data-testid="product-enquire-link"
              to="/commissions"
              className="font-mono text-[10px] tracking-[0.25em] text-bone-dim underline-offset-4 transition-colors hover:text-brass hover:underline"
            >
              {t("product.commissionCta")}
            </Link>
          </p>
          <ShareButtons art={art} title={title} />
        </Reveal>
      </div>
    </div>
  );
}
